import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import './OrderDetailsModal.css';

interface OrderItem {
  id: string;
  quantity: number;
  price: number;
  notes?: string | null;
  customization?: Record<string, string | string[]> | null;
  createdAt?: string;
  product: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    category: string;
  };
}

interface Payment {
  id: string;
  amount: number;
  method: string;
  createdAt: string;
}

interface Customer {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface UpsellRecommendation {
  id: string;
  productId: string;
  purchasedAt: string | null;
  product: {
    id: string;
    name: string;
  };
}

interface OrderDetails {
  id: string;
  status: 'PENDING' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED';
  totalAmount: number;
  notes: string | null;
  items: OrderItem[];
  payments: Payment[];
  customer: Customer | null;
  restaurant: {
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  qrCode: string;
  qrCodeUrl: string;
  upsells?: UpsellRecommendation[];
}

interface OrderDetailsModalProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate?: () => void;
}

function OrderDetailsModal({ orderId, isOpen, onClose, onStatusUpdate }: OrderDetailsModalProps) {
  const { t } = useTranslation();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchOrderDetails();
    }
  }, [isOpen, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`);
      setOrder(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'modals.failedLoadOrder');
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!order) return;
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status: newStatus });
      await fetchOrderDetails();
      onStatusUpdate?.();
    } catch (err: any) {
      alert(err.response?.data?.error || t('modals.failedUpdateStatus'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(getDateLocale(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      PENDING: 'status-pending',
      PREPARING: 'status-preparing',
      READY: 'status-ready',
      COMPLETED: 'status-completed',
      CANCELLED: 'status-cancelled',
    };
    return classes[status] || '';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      PENDING: t('orders.inQueue'),
      PREPARING: t('orders.preparing'),
      READY: t('orders.ready'),
      COMPLETED: t('orders.completed'),
      CANCELLED: t('orderStatus.cancelled'),
    };
    return labels[status] || status;
  };

  if (!isOpen) return null;

  return (
    <div className="order-details-overlay" onClick={onClose}>
      <div className="order-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-details-header">
          <div>
            <h2>{t('modals.orderDetails')}</h2>
            {order && (
              <div className="order-header-meta">
                <span className="order-number-large">#{order.id.slice(-6).toUpperCase()}</span>
                <span className={`status-badge-large ${getStatusClass(order.status)}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
            )}
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="order-details-body">
          {loading ? (
            <div className="loading-state">{t('modals.loadingOrderDetails')}</div>
          ) : error ? (
            <div className="error-state">{error.startsWith('modals.') ? t(error) : error}</div>
          ) : order ? (
            <>
              {/* Order Info Section */}
              <div className="details-section">
                <h3 className="section-title">{t('modals.orderInfo')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">{t('modals.restaurant')}:</span>
                    <span className="info-value">{order.restaurant.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('modals.created')}:</span>
                    <span className="info-value">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('modals.lastUpdated')}:</span>
                    <span className="info-value">{formatDate(order.updatedAt)}</span>
                  </div>
                  {order.completedAt && (
                    <div className="info-item">
                      <span className="info-label">{t('modals.completed')}:</span>
                      <span className="info-value">{formatDate(order.completedAt)}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">{t('modals.qrCode')}:</span>
                    <span className="info-value qr-code-value">{order.qrCode}</span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              {order.customer && (
                <div className="details-section">
                  <h3 className="section-title">{t('modals.customer')}</h3>
                  <div className="info-grid">
                    {order.customer.name && (
                      <div className="info-item">
                        <span className="info-label">{t('modals.name')}:</span>
                        <span className="info-value">{order.customer.name}</span>
                      </div>
                    )}
                    {order.customer.email && (
                      <div className="info-item">
                        <span className="info-label">{t('common.email')}:</span>
                        <span className="info-value">{order.customer.email}</span>
                      </div>
                    )}
                    {order.customer.phone && (
                      <div className="info-item">
                        <span className="info-label">{t('modals.phone')}:</span>
                        <span className="info-value">{order.customer.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items - Separated by Original and Upsells */}
              <div className="details-section">
                <h3 className="section-title">{t('modals.orderItems')}</h3>
                
                {/* Separate original items from upsell items */}
                {(() => {
                  const orderCreatedAt = new Date(order.createdAt).getTime();
                  const originalItems = order.items.filter(item => {
                    const itemCreatedAt = new Date(item.createdAt ?? order.createdAt).getTime();
                    // Items created within 5 seconds of order creation are original
                    return Math.abs(itemCreatedAt - orderCreatedAt) < 5000;
                  });
                  
                  const upsellItems = order.items.filter(item => {
                    const itemCreatedAt = new Date(item.createdAt ?? order.createdAt).getTime();
                    // Items created more than 5 seconds after order creation are upsells
                    return itemCreatedAt - orderCreatedAt >= 5000;
                  });

                  const formatCustomization = (cust: Record<string, string | string[]> | null | undefined): string => {
                    if (!cust || Object.keys(cust).length === 0) return '';
                    return Object.entries(cust)
                      .map(([, v]) => (Array.isArray(v) ? v.join(', ') : v))
                      .filter(Boolean)
                      .join(' • ');
                  };

                  // Group by product + customization (so different customizations stay separate)
                  const groupedOriginalItems = originalItems.reduce((acc, item) => {
                    const key = `${item.product.id}::${JSON.stringify(item.customization || {})}`;
                    if (!acc[key]) {
                      acc[key] = {
                        ...item,
                        totalQuantity: 0,
                        totalPrice: 0,
                      };
                    }
                    acc[key].totalQuantity += item.quantity;
                    acc[key].totalPrice += item.price * item.quantity;
                    return acc;
                  }, {} as Record<string, any>);

                  const groupedUpsellItems = upsellItems.reduce((acc, item) => {
                    const key = `${item.product.id}::${JSON.stringify(item.customization || {})}`;
                    if (!acc[key]) {
                      acc[key] = {
                        ...item,
                        totalQuantity: 0,
                        totalPrice: 0,
                      };
                    }
                    acc[key].totalQuantity += item.quantity;
                    acc[key].totalPrice += item.price * item.quantity;
                    return acc;
                  }, {} as Record<string, any>);

                  const originalItemsList = Object.values(groupedOriginalItems);
                  const upsellItemsList = Object.values(groupedUpsellItems);

                  return (
                    <>
                      {/* Original Order Items */}
                      <div className="items-group">
                        <div className="items-group-header">
                          <span className="items-group-title">{t('modals.originalOrder')}</span>
                          <span className="items-group-count">({originalItemsList.length} {t('modals.items')})</span>
                        </div>
                        <div className="items-list">
                          {originalItemsList.map((item: any) => (
                            <div key={item.id} className="item-row item-original">
                              <div className="item-info">
                                <div className="item-name">
                                  {item.product.name}
                                  {item.customization && Object.keys(item.customization).length > 0 && (
                                    <span className="item-customization"> ({formatCustomization(item.customization)})</span>
                                  )}
                                </div>
                                <div className="item-meta">
                                  <span className="item-category">{item.product.category}</span>
                                  {item.notes && (
                                    <span className="item-notes">{t('modals.note')}: {item.notes}</span>
                                  )}
                                </div>
                              </div>
                              <div className="item-quantity">×{item.totalQuantity}</div>
                              <div className="item-price">₸{item.totalPrice.toFixed(2)}</div>
                            </div>
                          ))}
                        </div>
                        {originalItemsList.length > 0 && (
                          <div className="items-group-total">
                            <span className="total-label">{t('modals.originalTotal')}:</span>
                            <span className="total-value">
                              ₸{originalItemsList.reduce((sum: number, item: any) => sum + item.totalPrice, 0).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Upsell Items */}
                      {upsellItemsList.length > 0 && (
                        <div className="items-group items-group-upsells">
                          <div className="items-group-header">
                            <span className="items-group-title">
                              <span className="upsell-badge"></span> {t('modals.additionalItems')}
                            </span>
                            <span className="items-group-count">({upsellItemsList.length} {t('modals.items')})</span>
                          </div>
                          <div className="items-list">
                            {upsellItemsList.map((item: any) => (
                              <div key={item.id} className="item-row item-upsell">
                                <div className="item-info">
                                  <div className="item-name">
                                    {item.product.name}
                                    {item.customization && Object.keys(item.customization).length > 0 && (
                                      <span className="item-customization"> ({formatCustomization(item.customization)})</span>
                                    )}
                                  </div>
                                  <div className="item-meta">
                                    <span className="item-category">{item.product.category}</span>
                                    {item.notes && (
                                      <span className="item-notes">{t('modals.note')}: {item.notes}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="item-quantity">×{item.totalQuantity}</div>
                                <div className="item-price">₸{item.totalPrice.toFixed(2)}</div>
                              </div>
                            ))}
                          </div>
                          <div className="items-group-total items-group-total-upsells">
                            <span className="total-label">{t('modals.upsellsTotal')}:</span>
                            <span className="total-value">
                              ₸{upsellItemsList.reduce((sum: number, item: any) => sum + item.totalPrice, 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Grand Total */}
                      <div className="items-total items-total-grand">
                        <span className="total-label">{t('modals.grandTotal')}:</span>
                        <span className="total-value">₸{order.totalAmount.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Payments */}
              {order.payments && order.payments.length > 0 && (
                <div className="details-section">
                  <h3 className="section-title">{t('modals.payments')} ({order.payments.length})</h3>
                  <div className="payments-list">
                    {order.payments.map((payment) => (
                      <div key={payment.id} className="payment-row">
                        <div className="payment-info">
                          <span className="payment-method">{payment.method.toUpperCase()}</span>
                          <span className="payment-date">{formatDate(payment.createdAt)}</span>
                        </div>
                        <span className="payment-amount">₸{payment.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="payments-total">
                    <span className="total-label">{t('modals.totalPaid')}:</span>
                    <span className="total-value">
                      ₸{order.payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Notes */}
              {order.notes && (
                <div className="details-section">
                  <h3 className="section-title">{t('modals.notes')}</h3>
                  <div className="notes-content">{order.notes}</div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="details-section">
                <h3 className="section-title">{t('modals.quickActions')}</h3>
                <div className="action-buttons-grid">
                  <button
                    className={`action-btn ${order.status === 'PENDING' ? 'active' : ''}`}
                    onClick={() => handleStatusUpdate('PENDING')}
                    disabled={updatingStatus}
                  >
                    {t('modals.queue')}
                  </button>
                  <button
                    className={`action-btn ${order.status === 'PREPARING' ? 'active' : ''}`}
                    onClick={() => handleStatusUpdate('PREPARING')}
                    disabled={updatingStatus}
                  >
                    {t('orders.preparing')}
                  </button>
                  <button
                    className={`action-btn ${order.status === 'READY' ? 'active' : ''}`}
                    onClick={() => handleStatusUpdate('READY')}
                    disabled={updatingStatus}
                  >
                    {t('orders.ready')}
                  </button>
                  <button
                    className={`action-btn ${order.status === 'COMPLETED' ? 'active' : ''}`}
                    onClick={() => handleStatusUpdate('COMPLETED')}
                    disabled={updatingStatus}
                  >
                    {t('modals.complete')}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default OrderDetailsModal;

